import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const EvidenceVideo = forwardRef(function EvidenceVideo({ vdoSource, autoPlay = true, muted = true, onEnded, onTime, onLoadedMeta }, ref) {
  const vref = useRef(null);
  const playerRef = useRef(null);


  useImperativeHandle(ref, () => ({
    play: () => playerRef.current?.play(),
    pause: () => playerRef.current?.pause(),
    paused: () => playerRef.current?.paused(),
    currentTime: (t) => (typeof t === 'number' ? playerRef.current?.currentTime(t) : playerRef.current?.currentTime()),
    duration: () => playerRef.current?.duration(),
    muted: (m) => (typeof m === 'boolean' ? (playerRef.current.muted(m), m) : playerRef.current?.muted()),
    volume: (val) =>
      typeof val === 'number' ? (playerRef.current.volume(Math.max(0, Math.min(1, val))), val) : playerRef.current?.volume(),
    el: () => playerRef.current?.el()
  }));

  // init once
  useEffect(() => {
    if (!vref.current) return;

    const player = (playerRef.current = videojs(vref.current, {
      controls: false, // custom UI
      autoplay: autoPlay && vdoSource, // Only autoplay if there's a source
      muted,
      preload: 'metadata',
      fluid: true,
      responsive: true,
      html5: {
        vhs: {
          overrideNative: true
        },
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false
      }
    }));

    const _ended = () => onEnded?.();
    const _time = () => onTime?.(player.currentTime() || 0);
    const _meta = () => onLoadedMeta?.(player.duration() || 0);

    player.on('ended', _ended);
    player.on('timeupdate', _time);
    player.on('loadedmetadata', _meta);

    return () => {
      player.off('ended', _ended);
      player.off('timeupdate', _time);
      player.off('loadedmetadata', _meta);
      player.dispose();
      playerRef.current = null;
    };
  }, []);

  // update source
  useEffect(() => {
    if (!playerRef.current || !vdoSource) return;
    const player = playerRef.current;
    console.log('EvidenceVideo: Setting video source:', vdoSource);
    
    const paused = player.paused();
    
    // Handle different video formats and sources
    let videoSources = [];
    
    if (vdoSource.includes('blob.core.windows.net')) {
      // For Azure blob URLs, try multiple formats
      videoSources = [
        { src: vdoSource, type: 'video/mp4' },
        { src: vdoSource, type: 'video/webm' },
        { src: vdoSource, type: 'video/ogg' }
      ];
    } else {
      videoSources = [{ src: vdoSource, type: 'video/mp4' }];
    }
    
    player.src(videoSources);
    
    // Add comprehensive error handling
    player.ready(() => {
      // Remove any existing listeners to prevent duplicates
      player.off('error');
      player.off('loadstart');
      player.off('loadeddata');
      player.off('canplay');
      
      player.on('error', () => {
        const error = player.error();
        console.error('Video.js error:', error);
        if (error) {
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
        }
      });
      
      player.on('loadstart', () => {
        console.log('Video load started');
      });
      
      player.on('loadeddata', () => {
        console.log('Video data loaded');
      });
      
      player.on('loadedmetadata', () => {
        console.log('Video metadata loaded');
      });
      
      player.on('canplay', () => {
        console.log('Video can start playing');
      });
      
      player.on('playing', () => {
        console.log('Video is playing');
      });
    });
    
    if (!paused) {
      player.play().catch((error) => {
        console.error('Play failed:', error);
      });
    }
  }, [vdoSource]);

  return <video ref={vref} className="video-js vjs-default-skin w-full h-full object-cover" />;
});

export default EvidenceVideo;
